import { useEffect, useContext } from 'react';

import './AssessmentStyle.css';

import { useTranslation } from 'react-i18next';

import { Alert } from '../../shared/components/alert/Alert';
import { AuthContext } from '../../shared/context/auth-context';
import { Footer } from '../footer/Footer';
import { Alcohol1 } from './Alcohol1';
import { AssessmentProfile } from './AssessmentProfile';
import { Behaviour1 } from './Behaviour1';
import { Completed } from './Completed';
import { Energy1 } from './Energy1';
import { Energy2 } from './Energy2';
import { Energy3 } from './Energy3';
import { FinancialWellbeing } from './FinancialWellbeing';
import { Hydration1 } from './Hydration1';
import { MentalHealth2 } from './MentalHealth2';
import { MentalHealth3 } from './MentalHealth3';
import { MentalHealth4 } from './MentalHealth4';
import { MentalHealth5 } from './MentalHealth5';
import { Nutrition1 } from './Nutrition1';
import { PhysicalActivity1 } from './PhysicalActivity1';
import { PhysicalHealth1 } from './PhysicalHealth1';
import { Sleep1 } from './Sleep1';
import { Stress1 } from './Stress1';

type AssessmentFormContainerProps = {
  assessmentValues: any;
  errorSummary: string[];
  pageOptions: any;
  regionContent: any;
  repeatAssessment: boolean;
  openModal: (html?: any) => void;
  saveAssessmentValue: (values: any) => void;
  navigateNextPageHandler: (values?: any) => void;
  navigatePrevPageHandler: () => void;
  goals: any;
};

export function AssessmentFormContainer({
  assessmentValues,
  errorSummary,
  pageOptions,
  regionContent,
  repeatAssessment,
  openModal,
  saveAssessmentValue,
  navigateNextPageHandler,
  navigatePrevPageHandler,
  goals,
}: AssessmentFormContainerProps) {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();

  useEffect(() => {
    const handleFocus = (e) => {
      const focusedElement = e.target;
      const footerOffset = 160;
      const rect = focusedElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      if (rect.bottom > viewportHeight - footerOffset) {
        const scrollNeeded = rect.bottom - (viewportHeight - footerOffset) + 60;
        window.scrollBy({
          top: scrollNeeded,
          behavior: 'smooth',
        });
      }
    };

    const rightContainer = document.getElementById('right_container');
    if (rightContainer) {
      rightContainer.addEventListener('focusin', handleFocus);
    }

    return () => {
      if (rightContainer) {
        rightContainer.removeEventListener('focusin', handleFocus);
      }
    };
  }, []);

  return (
    <>
      <div id="right_container">
        {errorSummary.length > 0 && (
          <Alert
            id="error_summary"
            isAssessive
            isVisible
            message={
              <ol>
                {errorSummary.map((error) => (
                  <li key={error}>
                    <a href={`#${error}`}>{t(`assessment.question.${error}`)}</a>
                  </li>
                ))}
              </ol>
            }
            title={{
              text:
                errorSummary.length > 1
                  ? t('error.summary.title.many', { errorCount: errorSummary.length })
                  : t('error.summary.title.one'),
              showTitle: true,
            }}
            type="error"
          />
        )}

        {auth.isLoggedIn && (
          <>
            {pageOptions.currentPage === 'AssessmentProfile' && (
              <AssessmentProfile
                questionid="AssessmentProfile"
                active=""
                questionHeading={
                  repeatAssessment ? t('A073') + auth.fname : t('A074') + auth.fname + t('A075')
                }
                questionText={repeatAssessment ? t('A076') : t('A077')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
              />
            )}
            {pageOptions.currentPage === 'PhysicalHealth1' && (
              <PhysicalHealth1
                questionid="PhysicalHealth1"
                active=""
                questionHeading={t('A078')}
                questionText={t('A079')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
              />
            )}

            {pageOptions.currentPage === 'PhysicalActivity1' && (
              <PhysicalActivity1
                questionid="PhysicalActivity1"
                active=""
                questionHeading={t('A080')}
                questionText={t('A081')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
              />
            )}
            {pageOptions.currentPage === 'Sleep1' && (
              <Sleep1
                questionid="Sleep1"
                active=""
                questionHeading={t('A082')}
                questionText={t('A083')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
              />
            )}

            {pageOptions.currentPage === 'Stress1' && (
              <Stress1
                questionid="Stress1"
                active=""
                questionHeading={t('A084')}
                questionText={t('A085')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
              />
            )}

            {pageOptions.currentPage === 'MentalHealth2' && (
              <MentalHealth2
                questionid="MentalHealth2"
                active=""
                questionHeading={t('A086')}
                questionText={t('A087')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
              />
            )}
            {pageOptions.currentPage === 'MentalHealth3' && (
              <MentalHealth3
                questionid="MentalHealth3"
                active=""
                questionHeading={t('A088')}
                questionText={t('A089')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
              />
            )}
            {pageOptions.currentPage === 'MentalHealth4' && (
              <MentalHealth4
                questionid="MentalHealth4"
                active=""
                questionHeading={t('A091')}
                questionText={t('A092')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
                regionContent={regionContent}
              />
            )}
            {pageOptions.currentPage === 'MentalHealth5' && (
              <MentalHealth5
                questionid="MentalHealth5"
                active=""
                questionHeading={t('A094')}
                questionText={t('A095')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
              />
            )}

            {pageOptions.currentPage === 'FinancialWellbeing1' && (
              <FinancialWellbeing
                questionid="FinancialWellbeing1"
                active=""
                questionHeading={t('A096')}
                questionText={t('A097')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
              />
            )}
            {pageOptions.currentPage === 'Nutrition1' && (
              <Nutrition1
                questionid="Nutrition1"
                active=""
                questionHeading={t('A098')}
                questionText={t('A099')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
                regionContent={regionContent}
              />
            )}
            {pageOptions.currentPage === 'Hydration1' && (
              <Hydration1
                questionid="Hydration1"
                active=""
                questionHeading={t('A100')}
                questionText={t('A101')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
              />
            )}
            {pageOptions.currentPage === 'Alcohol1' && (
              <Alcohol1
                questionid="Alcohol1"
                active=""
                questionHeading={t('A102')}
                questionText={t('A103')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
              />
            )}
            {pageOptions.currentPage === 'Energy1' && (
              <Energy1
                questionid="Energy1"
                active=""
                questionHeading={t('A104')}
                questionText={t('A105')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
              />
            )}
            {auth.access !== '2' && pageOptions.currentPage === 'Energy2' && (
              <Energy2
                questionid="Energy2"
                active=""
                questionHeading={t('A106')}
                questionText={t('A108')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
              />
            )}
            {auth.access !== '2' && pageOptions.currentPage === 'Energy3' && (
              <Energy3
                questionid="Energy3"
                active=""
                questionHeading={t('A109')}
                questionText={t('A110')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
              />
            )}
            {pageOptions.currentPage === 'Behaviour1' && (
              <Behaviour1
                questionid="Behaviour1"
                active=""
                questionHeading={t('A111')}
                questionText={t('A112')}
                assessmentValues={assessmentValues}
                onSetValue={saveAssessmentValue}
                onOpenModal={openModal}
              />
            )}

            {pageOptions.currentPage === 'Completed' && (
              <Completed
                questionid="Completed"
                questionHeading={t('A113')}
                questionText={t('A114')}
                assessmentValues={assessmentValues}
              />
            )}
          </>
        )}
      </div>
      <Footer
        onNextPage={navigateNextPageHandler}
        onPrevPage={navigatePrevPageHandler}
        goals={goals}
        iProgress={assessmentValues.iProgress}
      />
    </>
  );
}
